import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Download, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { CONTRACT_SCHEMAS } from "../data/contractSchemas";
import { printLetterhead } from "../lib/printContract";
import ContractRender from "../components/ContractRender";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function Contracts() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const [types, setTypes] = useState([]);
  const [contractType, setContractType] = useState("");
  const [details, setDetails] = useState({});
  const [meta, setMeta] = useState({
    currency: "KWD",
    effective_date: "",
  });
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/contracts/types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  // If launched via /contracts?contractId=xxx, fetch that contract
  // and populate the output panel so the user can re-print or copy it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("contractId");
    if (!id) return;
    api.get(`/portal/contracts/${id}`)
      .then((r) => {
        if (r.data?.content) {
          // populate type first; the reset effect would otherwise clear
          // the body. We schedule the body in a microtask so it lands
          // AFTER the reset has run.
          if (r.data.contract_type) setContractType(r.data.contract_type);
          setTimeout(() => setOutput(r.data.content), 0);
        }
      })
      .catch(() => {});
  }, []);

  // reset details when contract type changes
  useEffect(() => {
    setDetails({});
    setOutput("");
  }, [contractType]);

  const schema = CONTRACT_SCHEMAS[contractType] || [];

  const t = useMemo(() => ({
    eyebrow: isAr ? "صياغة العقود بالذكاء الاصطناعي" : "AI Contract Drafting",
    title: isAr ? "اصنع عقدًا احترافيًا في دقائق." : "Draft a professional contract in minutes.",
    lead: isAr
      ? "اختر النوع، وستظهر لك الحقول المطلوبة لذلك العقد. املأ التفاصيل، وسينتج النظام الذكي عقدًا كاملاً بصياغة قانونية بالعربية أو الإنجليزية. تتم مراجعة جميع المسودات من قبل محامٍ قبل التوقيع."
      : "Pick a type and the relevant fields appear. Fill them in and the AI generates a complete, legally structured contract in Arabic or English. Every draft must be reviewed by a qualified attorney before signature.",
    contractType: isAr ? "نوع العقد" : "Contract type",
    pickType: isAr ? "اختر النوع أولًا" : "Select a type first",
    pickTypePh: isAr ? "اختر النوع" : "Select a type",
    currency: isAr ? "العملة" : "Currency",
    effective: isAr ? "تاريخ النفاذ" : "Effective date",
    language: isAr ? "اللغة: العربية" : "Language: English",
    generate: isAr ? "أنشئ العقد" : "Generate contract",
    generating: isAr ? "جاري الصياغة..." : "Drafting…",
    output: isAr ? "نص العقد" : "Contract draft",
    placeholder: isAr ? "ستظهر مسودة عقدك هنا بعد إنشائها." : "Your generated draft will appear here.",
    copy: isAr ? "نسخ" : "Copy",
    download: isAr ? "حفظ كـ PDF" : "Save as PDF",
    disclaimer: isAr
      ? "تنبيه: المسودات المُنتَجة تلقائيًا هي نقطة بداية فقط ولا تُغني عن مراجعة محامٍ مرخّص."
      : "Disclaimer: AI-generated drafts are a starting point only and do not replace review by a licensed attorney.",
    copied: isAr ? "تم النسخ" : "Copied",
    err: isAr ? "تعذّر إنشاء العقد. حاول مرة أخرى." : "Could not generate. Try again.",
    requiredMissing: isAr ? "يرجى تعبئة الحقول المطلوبة" : "Please fill in the required fields",
  }), [isAr]);

  const setField = (key, value) => setDetails((d) => ({ ...d, [key]: value }));

  const onGenerate = async (e) => {
    e.preventDefault();
    if (!contractType) return toast.error(t.pickType);
    // validate required fields
    for (const f of schema) {
      if (f.required && !String(details[f.key] || "").trim()) {
        return toast.error(t.requiredMissing);
      }
    }
    setBusy(true);
    setOutput("");
    try {
      // build a human label dict so the LLM sees readable keys
      const labeledDetails = {};
      for (const f of schema) {
        const v = details[f.key];
        if (v === undefined || v === null || v === "") continue;
        const label = isAr ? f.ar : f.en;
        if (f.type === "select") {
          const opt = (f.options || []).find((o) => o.value === v);
          labeledDetails[label] = opt ? (isAr ? opt.ar : opt.en) : v;
        } else {
          labeledDetails[label] = v;
        }
      }

      const { data } = await api.post(
        "/contracts/generate",
        {
          contract_type: contractType,
          language: lang,
          details: labeledDetails,
          currency: meta.currency || undefined,
          effective_date: meta.effective_date || undefined,
        },
        { timeout: 180000 }
      );
      if (!data?.content || !data.content.trim()) {
        toast.error(
          isAr
            ? "تم توليد عقد فارغ، يُرجى المحاولة مرة أخرى."
            : "Contract came back empty. Please try again."
        );
        return;
      }
      setOutput(data.content);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = typeof detail === "string" ? detail : t.err;
      // Map ANY service-unavailable / quota-related status to a single
      // neutral public-facing message. Internal billing instructions
      // must never reach end-users.
      if (status === 402 || status === 502 || status === 503 || status === 504) {
        msg = isAr
          ? "الخدمة غير متاحة مؤقتاً. يُرجى المحاولة بعد قليل أو التواصل مع المكتب مباشرة."
          : "Service temporarily unavailable. Please try again shortly or contact the firm.";
      } else if (err?.code === "ECONNABORTED") {
        msg = isAr
          ? "انتهت مهلة الانتظار. يُرجى المحاولة مجدداً."
          : "Request timed out. Please retry.";
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    await navigator.clipboard.writeText(output);
    toast.success(t.copied);
  };

  const onDownload = async () => {
    if (!output) return;
    setDownloading(true);
    try {
      await printLetterhead({ content: output, language: lang });
    } catch {
      toast.error("PDF export failed.");
    } finally {
      setDownloading(false);
    }
  };

  const renderField = (f) => {
    const label = isAr ? f.ar : f.en;
    const ph = isAr ? f.ar_ph : f.en_ph;
    const value = details[f.key] ?? "";
    const required = !!f.required;
    const labelEl = (
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
        {label}{required ? " *" : ""}
      </Label>
    );

    if (f.type === "textarea") {
      return (
        <div key={f.key} className={f.full === false ? "" : "sm:col-span-2"}>
          {labelEl}
          <Textarea
            dir="auto"
            rows={3}
            placeholder={ph}
            value={value}
            onChange={(e) => setField(f.key, e.target.value)}
            data-testid={`contract-field-${f.key}`}
            className="mt-2 rounded-sm"
          />
        </div>
      );
    }

    if (f.type === "select") {
      return (
        <div key={f.key}>
          {labelEl}
          <Select value={value} onValueChange={(v) => setField(f.key, v)}>
            <SelectTrigger
              data-testid={`contract-field-${f.key}`}
              className="mt-2 rounded-sm h-11"
            >
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent>
              {(f.options || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {isAr ? o.ar : o.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    const inputType =
      f.type === "date" ? "date" : f.type === "number" ? "number" : "text";

    // Civil-ID fields ride alongside the related party — render them in
    // the same row at half-width so they pair visually with the name.
    const isCivil = f.key.endsWith("_civil_id");
    const wrapClass = isCivil ? "" : "";

    return (
      <div key={f.key} className={wrapClass}>
        {labelEl}
        <Input
          dir="auto"
          type={inputType}
          inputMode={f.inputMode}
          pattern={f.pattern}
          maxLength={f.maxLength}
          placeholder={ph}
          value={value}
          onChange={(e) => {
            // For civil-id fields, strip any non-digits silently so users
            // can't accidentally enter letters.
            const next = isCivil ? e.target.value.replace(/\D/g, "") : e.target.value;
            setField(f.key, next);
          }}
          data-testid={`contract-field-${f.key}`}
          className="mt-2 rounded-sm h-11"
        />
      </div>
    );
  };

  return (
    <div data-testid="contracts-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-7 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
          <p className="mt-6 text-xs text-slate-400 uppercase tracking-[0.18em]">{t.language}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* FORM */}
        <form onSubmit={onGenerate} className="lg:col-span-5 grid gap-5 content-start" data-testid="contract-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.contractType}</Label>
            <Select value={contractType} onValueChange={setContractType}>
              <SelectTrigger data-testid="contract-type" className="mt-2 rounded-sm h-11">
                <SelectValue placeholder={t.pickTypePh} />
              </SelectTrigger>
              <SelectContent>
                {types.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {isAr ? c.ar : c.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {schema.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 hairline pt-5">
              {schema.map(renderField)}
            </div>
          )}

          {schema.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 hairline pt-5">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.currency}</Label>
                <Select value={meta.currency} onValueChange={(v) => setMeta({ ...meta, currency: v })}>
                  <SelectTrigger data-testid="contract-currency" className="mt-2 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KWD">{isAr ? "د.ك — الدينار الكويتي" : "KWD — Kuwaiti Dinar"}</SelectItem>
                    <SelectItem value="SAR">{isAr ? "ر.س — الريال السعودي" : "SAR — Saudi Riyal"}</SelectItem>
                    <SelectItem value="AED">{isAr ? "د.إ — الدرهم الإماراتي" : "AED — UAE Dirham"}</SelectItem>
                    <SelectItem value="QAR">{isAr ? "ر.ق — الريال القطري" : "QAR — Qatari Riyal"}</SelectItem>
                    <SelectItem value="BHD">{isAr ? "د.ب — الدينار البحريني" : "BHD — Bahraini Dinar"}</SelectItem>
                    <SelectItem value="OMR">{isAr ? "ر.ع — الريال العماني" : "OMR — Omani Rial"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.effective}</Label>
                <Input
                  type="date"
                  value={meta.effective_date}
                  onChange={(e) => setMeta({ ...meta, effective_date: e.target.value })}
                  data-testid="contract-date"
                  className="mt-2 rounded-sm h-11"
                />
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !contractType}
            data-testid="contract-generate"
            className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 me-2 animate-spin" /> {t.generating}</>
            ) : (
              <><Sparkles className="w-4 h-4 me-2" /> {t.generate}</>
            )}
          </Button>

          <p className="text-xs text-slate-400 leading-relaxed mt-2">{t.disclaimer}</p>
        </form>

        {/* OUTPUT */}
        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-slate-50 min-h-[640px] flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-3 bg-white">
              <p className="eyebrow">{t.output}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onCopy}
                  disabled={!output}
                  data-testid="contract-copy"
                  className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" /> {t.copy}
                </button>
                <button
                  onClick={onDownload}
                  disabled={!output || downloading}
                  data-testid="contract-download"
                  className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {downloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}{" "}
                  {t.download}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-8 lg:p-10">
              {busy && !output ? (
                <div className="h-full flex items-center justify-center text-slate-400">
                  <Loader2 className="w-5 h-5 animate-spin me-3" /> {t.generating}
                </div>
              ) : output ? (
                <ContractRender content={output} isAr={isAr} />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-center px-10">
                  {t.placeholder}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
